import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from '../../utils/formatters';
import useFocusTrap from '../../hooks/useFocusTrap';
import useScrollLock from '../../hooks/useScrollLock';
import IconButton from './IconButton';
import { ModalHeader, ModalBody, ModalFooter } from './ModalParts';

const SIZE_CLASSES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-5xl',
  full: 'max-w-none w-full h-full rounded-none',
};

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const Spinner = () => (
  <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

/**
 * Accessible modal dialog. Renders through a portal to document.body.
 *
 * Handles: role/aria wiring, focus trap + restore, Escape, body scroll lock, safe
 * overlay-click dismissal, a loading state, responsive sizing, and reduced-motion.
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  loading = false,
  footer,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  hideCloseButton = false,
  initialFocusRef,
  className,
  children,
}) {
  const panelRef = useRef(null);
  const overlayMouseDownTarget = useRef(null);
  const reactId = useId();
  const titleId = `modal-title-${reactId}`;
  const descId = `modal-desc-${reactId}`;

  useScrollLock(isOpen);
  useFocusTrap(panelRef, isOpen, initialFocusRef);

  // Escape to close. Bound at the document level so it works regardless of focus.
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeOnEscape, onClose]);

  if (!isOpen) return null;

  // Close only when the pointer press both started and ended on the overlay itself,
  // so a text-selection drag that ends outside the panel doesn't dismiss the dialog.
  const handleOverlayMouseDown = (e) => {
    overlayMouseDownTarget.current = e.target;
  };
  const handleOverlayMouseUp = (e) => {
    const startedOnOverlay = overlayMouseDownTarget.current === e.currentTarget;
    overlayMouseDownTarget.current = null;
    if (closeOnOverlayClick && startedOnOverlay && e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const labelledBy = title ? titleId : undefined;
  const describedBy = description ? descId : undefined;

  return createPortal(
    <div
      className="modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        aria-label={title ? undefined : 'Dialog'}
        aria-busy={loading || undefined}
        tabIndex={-1}
        className={classNames(
          'modal-panel relative bg-white shadow-2xl w-full mx-auto flex flex-col',
          'max-h-[90vh] rounded-2xl',
          SIZE_CLASSES[size] || SIZE_CLASSES.md,
          className,
        )}
      >
        {(title || !hideCloseButton) && (
          <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200">
            <div className="min-w-0">
              {title && (
                <h2 id={titleId} className="text-xl font-bold text-gray-800 truncate">
                  {title}
                </h2>
              )}
              {description && (
                <p id={descId} className="text-sm text-gray-600 mt-1">
                  {description}
                </p>
              )}
            </div>
            {!hideCloseButton && (
              <IconButton label="Close dialog" onClick={onClose} className="-mr-2 -mt-1 shrink-0">
                <CloseIcon />
              </IconButton>
            )}
          </div>
        )}

        <div className="px-6 py-5 overflow-y-auto custom-scrollbar flex-1">{children}</div>

        {footer && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap gap-3 justify-end">
            {footer}
          </div>
        )}

        {loading && (
          <div
            className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center rounded-2xl"
            aria-hidden="true"
          >
            <Spinner />
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
